import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { Coins, Users2, Lock, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TABS = [
    { k: "residuals", label: "Residuals Ledger", Icon: Coins },
    { k: "fractional", label: "Fractional Ownership", Icon: Users2 },
    { k: "backroom", label: "Unreleased Backroom", Icon: Lock },
    { k: "credit", label: "Creator Credit", Icon: CreditCard },
    { k: "guild", label: "Guild Escrow", Icon: ShieldCheck },
];

export default function CreatorEconomyHub() {
    const { user } = useAuth();
    const [tab, setTab] = useState("residuals");
    const [residuals, setResiduals] = useState([]);
    const [resForm, setResForm] = useState({ film_id: "", beneficiary_name: "", beneficiary_role: "director", percent: 5 });
    const [films, setFilms] = useState([]);
    const [offerings, setOfferings] = useState([]);
    const [portfolio, setPortfolio] = useState([]);
    const [offForm, setOffForm] = useState({ film_id: "", total_units: 1000, price_per_unit_usd: 100 });
    const [credit, setCredit] = useState(null);

    const reload = async () => {
        try {
            const f = await api.get("/films"); setFilms(f.data);
            if (f.data[0] && !resForm.film_id) setResForm(r => ({ ...r, film_id: f.data[0].id }));
            if (f.data[0] && !offForm.film_id) setOffForm(o => ({ ...o, film_id: f.data[0].id }));
        } catch (_) {}
        if (user?.role === "creator") {
            try { const r = await api.get("/residuals/mine"); setResiduals(r.data); } catch (_) {}
            try { const c = await api.get("/credit/status"); setCredit(c.data); } catch (_) {}
        }
        try { const o = await api.get("/fractional/offerings"); setOfferings(o.data); } catch (_) {}
        try { const p = await api.get("/fractional/portfolio"); setPortfolio(p.data); } catch (_) {}
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?.role]);

    const addResidual = async (e) => {
        e.preventDefault();
        try {
            await api.post("/residuals", { ...resForm, percent: Number(resForm.percent) });
            toast.success("Residual recorded");
            reload();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    const createOffering = async (e) => {
        e.preventDefault();
        try {
            await api.post("/fractional/offer", { ...offForm, total_units: Number(offForm.total_units), price_per_unit_usd: Number(offForm.price_per_unit_usd) });
            toast.success("Fractional offering live");
            reload();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    const invest = async (off, units) => {
        try {
            await api.post("/fractional/invest", { offering_id: off.id, units: Number(units) });
            toast.success(`Acquired ${units} units of ${off.film_title}`);
            reload();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    const applyCredit = async () => {
        try { const { data } = await api.post("/credit/apply"); setCredit({ applications: [data, ...(credit?.applications || [])], latest: data }); toast.success("Application processed"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    const requestBackroom = async () => {
        try { await api.post("/backroom/request-access"); toast.success("Access request submitted"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    const toggleGuild = async (fid) => {
        try { await api.post(`/guild-escrow/${fid}/toggle`); toast.success("Guild escrow toggled"); reload(); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                    <Coins className="w-4 h-4" /> Creator Economics
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-3">Solve the <span className="silver-text italic">creator</span> side.</h1>
                <p className="text-silver-400 mb-10 max-w-2xl">Residuals ledger, fractional ownership, private backroom, creator credit, and guild-sanctioned escrow — the economics layer the rest of the industry ignores.</p>

                <div className="flex items-center gap-6 border-b border-white/5 mb-8 overflow-x-auto">
                    {TABS.map(({ k, label, Icon }) => (
                        <button key={k} onClick={() => setTab(k)}
                            className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap text-xs uppercase tracking-widest ${
                                tab === k ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"
                            }`} data-testid={`econ-tab-${k}`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {tab === "residuals" && (
                    <div data-testid="residuals-panel" className="grid md:grid-cols-2 gap-8">
                        <form onSubmit={addResidual} className="border border-white/10 rounded-sm p-6 bg-ink-200/40">
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-4">Add residual beneficiary</div>
                            <label className="block text-xs uppercase tracking-widest text-silver-400 mb-1">Film</label>
                            <select value={resForm.film_id} onChange={(e) => setResForm({ ...resForm, film_id: e.target.value })} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-3">
                                {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                            </select>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <input placeholder="Beneficiary name" value={resForm.beneficiary_name} onChange={(e) => setResForm({ ...resForm, beneficiary_name: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" required />
                                <select value={resForm.beneficiary_role} onChange={(e) => setResForm({ ...resForm, beneficiary_role: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm">
                                    <option value="director">Director</option>
                                    <option value="writer">Writer</option>
                                    <option value="composer">Composer</option>
                                    <option value="cast">Cast</option>
                                </select>
                            </div>
                            <input type="number" step="0.1" placeholder="% share" value={resForm.percent} onChange={(e) => setResForm({ ...resForm, percent: e.target.value })} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" />
                            <button type="submit" className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="residual-add-btn">Record</button>
                        </form>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Ledger</div>
                            {residuals.length === 0 ? <div className="text-silver-500 text-sm">No residuals recorded yet.</div> : (
                                <div className="space-y-2">
                                    {residuals.map((r) => (
                                        <div key={r.id} className="flex items-center justify-between p-3 border border-white/10 rounded-sm bg-ink-200/40">
                                            <div>
                                                <div className="text-silver-200 text-sm">{r.beneficiary_name}</div>
                                                <div className="text-[10px] tracking-widest uppercase text-silver-500">{r.beneficiary_role} · {r.film_title}</div>
                                            </div>
                                            <div className="text-platinum text-xl font-display">{r.percent}%</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "fractional" && (
                    <div data-testid="fractional-panel">
                        {user?.role === "creator" && (
                            <form onSubmit={createOffering} className="border border-white/10 rounded-sm p-6 bg-ink-200/40 mb-8">
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-4">Create offering</div>
                                <div className="grid md:grid-cols-3 gap-3 mb-3">
                                    <select value={offForm.film_id} onChange={(e) => setOffForm({ ...offForm, film_id: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm">
                                        {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                                    </select>
                                    <input type="number" placeholder="Units (50-10000)" value={offForm.total_units} onChange={(e) => setOffForm({ ...offForm, total_units: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" />
                                    <input type="number" placeholder="Price / unit USD" value={offForm.price_per_unit_usd} onChange={(e) => setOffForm({ ...offForm, price_per_unit_usd: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" />
                                </div>
                                <button type="submit" className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="fractional-offer-btn">Open offering</button>
                                <div className="text-[10px] text-silver-500 mt-2">MOCKED compliance — real launch requires SEC Reg CF or SEBI sandbox registration</div>
                            </form>
                        )}
                        <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Live offerings</div>
                        {offerings.length === 0 ? <div className="text-silver-500 text-sm">No offerings open.</div> : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {offerings.map((o) => {
                                    const sold = o.units_sold / o.total_units * 100;
                                    return (
                                        <div key={o.id} className="border border-white/10 rounded-sm p-5 bg-ink-200/40">
                                            <div className="font-display text-xl mb-2">{o.film_title}</div>
                                            <div className="text-[11px] text-silver-500 mb-3">{o.total_units.toLocaleString()} units @ ${o.price_per_unit_usd}</div>
                                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-2">
                                                <div className="h-full bg-platinum" style={{ width: `${sold}%` }} />
                                            </div>
                                            <div className="text-[11px] text-silver-400 mb-3">{o.units_sold}/{o.total_units} sold ({sold.toFixed(1)}%)</div>
                                            <button onClick={() => {
                                                const u = window.prompt(`Units to acquire (max ${o.total_units - o.units_sold}):`, "10");
                                                if (u) invest(o, u);
                                            }} className="bg-platinum hover:bg-white text-ink-400 px-4 py-2 text-[11px] tracking-widest uppercase rounded-sm w-full" data-testid={`invest-${o.id}`}>Invest</button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {portfolio.length > 0 && (
                            <div className="mt-10">
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Your portfolio</div>
                                <div className="space-y-2">
                                    {portfolio.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between p-3 border border-white/10 rounded-sm">
                                            <div className="text-silver-200 text-sm">{p.film_title}</div>
                                            <div className="text-silver-300">{p.units} units · <span className="text-platinum tabular-nums">${p.amount_usd.toLocaleString()}</span></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "backroom" && (
                    <div data-testid="backroom-panel" className="max-w-2xl">
                        <div className="border border-platinum/30 bg-platinum/5 rounded-sm p-6 mb-6">
                            <Lock className="w-6 h-6 text-platinum mb-3" />
                            <div className="font-display text-2xl mb-2">Unreleased Backroom</div>
                            <div className="text-silver-400 text-sm mb-4">Private marketplace for in-development scripts, shelved studio films, and unreleased titles. Access is gated to verified buyers over $500k annual transaction volume.</div>
                            <button onClick={requestBackroom} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="backroom-request-btn">Request access</button>
                        </div>
                    </div>
                )}

                {tab === "credit" && (
                    <div data-testid="credit-panel" className="max-w-2xl">
                        <button onClick={applyCredit} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm mb-6" data-testid="credit-apply-btn">Check eligibility</button>
                        {credit?.latest && (
                            <div className="border border-white/10 rounded-sm p-5 bg-ink-200/40">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-[10px] tracking-widest uppercase text-silver-500">Latest application</div>
                                    <div className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm border ${credit.latest.eligible ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/5" : "border-amber-300/40 text-amber-300 bg-amber-300/5"}`}>
                                        {credit.latest.status.replace("_", " ")}
                                    </div>
                                </div>
                                <div className="font-display text-5xl silver-text tabular-nums">${Number(credit.latest.credit_limit_usd).toLocaleString()}</div>
                                <div className="text-silver-400 text-sm mt-1">credit limit · {credit.latest.rate_apr ? `${credit.latest.rate_apr}% APR` : "needs 3+ closed deals"}</div>
                                <div className="text-[11px] text-silver-500 mt-4">Based on {credit.latest.closed_deals} closed deals · MOCKED underwriting</div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "guild" && (
                    <div data-testid="guild-panel">
                        <p className="text-silver-400 text-sm mb-6 max-w-2xl">Partner with WGA / SAG-AFTRA / IATSE for strike-proof escrow. When a union strike is active, payments hold and release per guild rules.</p>
                        <div className="space-y-2">
                            {films.filter(f => f.creator_id === user?.id).map((f) => (
                                <div key={f.id} className="flex items-center justify-between p-4 border border-white/10 rounded-sm bg-ink-200/40">
                                    <div>
                                        <div className="text-silver-200 text-sm">{f.title}</div>
                                        <div className="text-[10px] tracking-widest uppercase text-silver-500">{f.country}</div>
                                    </div>
                                    <button onClick={() => toggleGuild(f.id)} className={`px-4 py-2 rounded-sm text-[11px] tracking-widest uppercase ${f.guild_escrow_enabled ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30" : "border border-white/15 text-silver-300"}`} data-testid={`guild-toggle-${f.id}`}>
                                        {f.guild_escrow_enabled ? "Enabled ✓" : "Enable"}
                                    </button>
                                </div>
                            ))}
                            {films.filter(f => f.creator_id === user?.id).length === 0 && (
                                <div className="text-silver-500 text-sm">Only creators can enable guild escrow on their films.</div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
